import { useEffect, useRef, useState } from 'react';

import type { ContactScanResponse } from '../../lib/api';
import { apiClient } from '../../lib/api';

export type ScannedPayload = {
  response: ContactScanResponse;
  /** The exact data URL the user previewed before upload — surfaced to the
   *  parent so the preview modal can re-display the thumbnail. */
  thumbnailDataUrl: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onScanned: (payload: ScannedPayload) => void;
  /**
   * "camera" → live mobile capture (default).
   * "file"   → desktop fallback: pick an image from disk and upload directly.
   */
  mode?: 'camera' | 'file';
};

type Phase =
  | 'permission'
  | 'denied'
  | 'streaming'
  | 'captured'
  | 'uploading'
  | 'error'
  | 'file-pick';

/**
 * Business-card scanner with two modes:
 *
 * 1. CAMERA (mobile / iPad)
 *    Live <video> + framing guide → capture → upload → OCR
 *
 * 2. FILE (desktop fallback)
 *    File picker → preview → upload → OCR. The endpoint is identical, so the
 *    RM gets the same extracted fields whether the picture comes from the
 *    iPhone camera or from a scanned PDF/JPG on the laptop.
 *
 * The image bytes are NEVER persisted server-side — only the SHA-256 hash
 * is written to the audit log (RGPD / nLPD).
 */
export function BusinessCardScanner({ open, onClose, onScanned, mode = 'camera' }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [phase, setPhase] = useState<Phase>('permission');
  const [capturedDataUrl, setCapturedDataUrl] = useState<string | null>(null);
  const [capturedMime, setCapturedMime] = useState<'image/jpeg' | 'image/png' | 'image/webp'>(
    'image/jpeg',
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCapturedDataUrl(null);
    setErrorMsg(null);

    // File mode: don't request the camera, just present the picker prompt.
    // Note: we don't auto-trigger the hidden <input> here. Some browsers
    // (Safari notably) refuse to open the OS picker when .click() is
    // dispatched after async state transitions because the user gesture
    // is considered "lost". The visible "Parcourir…" button preserves it.
    if (mode === 'file') {
      setPhase('file-pick');
      return;
    }

    setPhase('permission');

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        if (!cancelled) {
          setPhase('error');
          setErrorMsg('Camera not available in this browser.');
        }
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setPhase('streaming');
      } catch (err) {
        if (!cancelled) {
          setPhase('denied');
          setErrorMsg(
            (err as Error)?.message ??
              'Camera permission denied. Enable it in your browser settings.',
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [open, mode]);

  function capture() {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth || 1280;
    c.height = v.videoHeight || 720;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, c.width, c.height);

    // F9 fix: canvas.toDataURL(...) used to ignore the requested mime on
    // some WebKit builds and emit WEBP under an image/jpeg header. The
    // backend then forwarded mismatched bytes/media_type to Anthropic and
    // the request was rejected. `toBlob` honours the mime accurately on
    // every modern engine; if it's missing we hard-fail instead of
    // silently shipping wrong-format bytes again.
    if (typeof c.toBlob !== 'function') {
      setPhase('error');
      setErrorMsg('Browser incompatible. Update Safari or Chrome and try again.');
      return;
    }
    c.toBlob(
      (blob) => {
        if (!blob) {
          setPhase('error');
          setErrorMsg('Capture failed. Try again.');
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          setCapturedMime('image/jpeg');
          setCapturedDataUrl(String(reader.result));
          setPhase('captured');
        };
        reader.onerror = () => {
          setPhase('error');
          setErrorMsg('Could not read the captured image.');
        };
        reader.readAsDataURL(blob);
      },
      'image/jpeg',
      0.85,
    );
  }

  function retake() {
    setCapturedDataUrl(null);
    if (mode === 'file') {
      setPhase('file-pick');
      // Same rationale as the initial mount: rely on the user clicking the
      // explicit Parcourir button instead of forcing the picker open.
    } else {
      setPhase('streaming');
    }
  }

  function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow reselecting the same file
    if (!file) {
      // User cancelled the OS picker — close the modal cleanly.
      onClose();
      return;
    }
    const mime = file.type;
    if (mime !== 'image/jpeg' && mime !== 'image/png' && mime !== 'image/webp') {
      setPhase('error');
      setErrorMsg('Unsupported format — pick a JPG, PNG or WEBP image.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCapturedMime(mime);
      setCapturedDataUrl(String(reader.result));
      setPhase('captured');
    };
    reader.onerror = () => {
      setPhase('error');
      setErrorMsg('Could not read the file.');
    };
    reader.readAsDataURL(file);
  }

  async function upload() {
    if (!capturedDataUrl) return;
    setPhase('uploading');
    setErrorMsg(null);
    try {
      const base64 = capturedDataUrl.replace(/^data:image\/\w+;base64,/, '');
      const response = await apiClient.scanBusinessCard({
        image_base64: base64,
        mime_type: capturedMime,
      });
      onScanned({ response, thumbnailDataUrl: capturedDataUrl });
    } catch (err) {
      setPhase('error');
      setErrorMsg(humanizeScanError(err));
    }
  }

  if (!open) return null;

  const isFile = mode === 'file';
  const title = isFile ? 'Import a business card' : 'Scan a business card';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 bg-graphite text-white flex flex-col"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <h2 className="font-display text-base font-semibold">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-xs uppercase tracking-[0.16em] text-white/70 hover:text-white min-h-touch min-w-touch flex items-center"
        >
          Cancel
        </button>
      </header>

      {/* Always-mounted file input. In `file` mode it auto-opens; in
          `camera` mode it stays hidden. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onFilePicked}
        className="hidden"
      />

      <div className="flex-1 relative overflow-hidden bg-black">
        {!isFile && phase !== 'captured' && (
          <video
            ref={videoRef}
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {phase === 'captured' && capturedDataUrl && (
          <img
            src={capturedDataUrl}
            alt="Scanned card preview"
            className="absolute inset-0 w-full h-full object-contain bg-black"
          />
        )}
        <canvas ref={canvasRef} className="hidden" />

        {!isFile && phase === 'streaming' && (
          <div
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <div className="w-[86%] aspect-[1.6/1] border-2 border-white/80 rounded-card shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
          </div>
        )}

        {phase === 'permission' && (
          <p className="absolute inset-0 flex items-center justify-center text-sm text-white/70 px-6 text-center">
            Requesting camera permission…
          </p>
        )}
        {phase === 'file-pick' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center gap-4">
            <p className="font-display text-xl font-semibold">Pick an image</p>
            <p className="text-sm text-white/70 max-w-sm">
              Select a photo or scan of a business card (JPG, PNG, WEBP). The image is never stored
              on the server — only its hash is logged.
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="btn-brand mt-2"
            >
              Browse…
            </button>
            <p className="text-[11px] text-white/40 max-w-sm mt-2 leading-relaxed">
              If <code className="font-tabular">ANTHROPIC_API_KEY</code> is not configured, the scan
              endpoint returns 503 and OCR is disabled.
            </p>
          </div>
        )}
        {phase === 'denied' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center gap-3">
            <p className="font-display text-xl font-semibold">Camera unavailable</p>
            <p className="text-sm text-white/70">
              {errorMsg ?? 'Enable camera permission and try again.'}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary mt-3 !text-white !border-white/40"
            >
              Close
            </button>
          </div>
        )}
        {phase === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center gap-3">
            <p className="font-display text-xl font-semibold text-signal">Failed</p>
            <p className="text-sm text-white/70">{errorMsg}</p>
            <button
              type="button"
              onClick={retake}
              className="btn-secondary !text-white !border-white/40"
            >
              Retry
            </button>
          </div>
        )}
      </div>

      <footer className="px-4 py-4 bg-graphite border-t border-white/10 flex items-center gap-3">
        {!isFile && phase === 'streaming' && (
          <button
            type="button"
            onClick={capture}
            className="flex-1 btn-brand"
            aria-label="Capture the card"
          >
            Capture
          </button>
        )}
        {phase === 'captured' && (
          <>
            <button
              type="button"
              onClick={retake}
              className="flex-1 btn-secondary !text-white !border-white/40"
            >
              {isFile ? 'Change file' : 'Retake'}
            </button>
            <button type="button" onClick={upload} className="flex-1 btn-brand">
              Extract info
            </button>
          </>
        )}
        {phase === 'uploading' && (
          <p className="flex-1 text-center text-sm text-white/70" aria-live="polite">
            Analysing…
          </p>
        )}
      </footer>
    </div>
  );
}

/**
 * Map the API error payload (the shape produced by the standardized scan
 * route in apps/api) to an English user-facing message. Falls back to a
 * generic retry message when the shape isn't recognised — the user must
 * never see a raw error code.
 */
function humanizeScanError(err: unknown): string {
  const e = err as {
    status?: number;
    payload?: { message?: string; code?: string; error?: string } | null;
  };
  const payload = e?.payload ?? null;
  if (payload?.message) return payload.message;
  if (e?.status === 413) return 'Image too large for the server. Compress or crop it.';
  if (e?.status === 429) return 'Service temporarily busy. Try again in 30 seconds.';
  if (e?.status === 502) return 'OCR service unreachable. Try again shortly.';
  if (e?.status === 503) return 'The scan service is not configured. Contact your administrator.';
  return 'OCR failed. Try again.';
}
